import type { IPencilOfferInputs, Offer, OfferType, OverridesFallbackSnapshot, PaymentTerm } from '@makemydeal/dr-platform-types';
import { BALLOON, FINANCE, LEASE, OFFER_ENCRYPTION_FIELDS, RESIDUAL_DOLLAR, RESIDUAL_PERCENT } from '@makemydeal/dr-platform-types';
import { getSelectedTerm } from '../../../selectors/offerNodeSelectors';
import { termHasImplausibleMoneyFactor } from '../../../utils/rateUtils';

export const FALLBACK_SNAPSHOT_FIELDS = [
    'sellRateOverride',
    'rateTypeOverride',
    'adjustedResidualAmountOverride',
    'adjustedResidualPercentOverride',
    'adjustedResidualTypeOverride',
    'baseResidualAmountOverride',
    'baseResidualPercentOverride',
    'baseResidualType',
    'baseResidualTypeOverride',
    'programResidualOverride',
    'programResidualPercentOverride',
    'balloonPaymentAmountOverride'
] as const;

type FallbackField = (typeof FALLBACK_SNAPSHOT_FIELDS)[number];

const RATE_OFFER_TYPES: ReadonlySet<OfferType> = new Set([FINANCE, LEASE, BALLOON]);

type ResidualAmountKey = 'baseResidualAmountOverride' | 'adjustedResidualAmountOverride';
type ResidualPercentKey = 'baseResidualPercentOverride' | 'adjustedResidualPercentOverride';
type ResidualTypeKey = 'baseResidualTypeOverride' | 'adjustedResidualTypeOverride';

const copyExistingOverrides = (node: IPencilOfferInputs): OverridesFallbackSnapshot => {
    const snap: OverridesFallbackSnapshot = {};
    FALLBACK_SNAPSHOT_FIELDS.forEach(<K extends FallbackField>(k: K) => {
        const value = node[k];
        if (value !== undefined) snap[k] = value;
    });
    return snap;
};

const hydrateRate = (snap: OverridesFallbackSnapshot, term: PaymentTerm, offerType: OfferType): void => {
    if (termHasImplausibleMoneyFactor(term, offerType)) return;

    if (snap.sellRateOverride == null && term.sellRate != null) {
        snap.sellRateOverride = term.sellRate;
    }
    if (snap.rateTypeOverride == null && term.rateType) {
        snap.rateTypeOverride = term.rateType;
    }
};

const hydrateResidualPair = (
    snap: OverridesFallbackSnapshot,
    preferPercent: boolean,
    termValues: { percent?: number; dollar?: number },
    keys: { amount: ResidualAmountKey; percent: ResidualPercentKey; type: ResidualTypeKey }
): 'percent' | 'dollar' | undefined => {
    const hasPercentOverride = snap[keys.percent] != null;
    const hasAmountOverride = snap[keys.amount] != null;

    if (hasPercentOverride || hasAmountOverride) {
        const unit: 'percent' | 'dollar' = hasPercentOverride ? 'percent' : 'dollar';
        if (snap[keys.type] == null) {
            snap[keys.type] = unit === 'percent' ? RESIDUAL_PERCENT : RESIDUAL_DOLLAR;
        }
        return unit;
    }

    if (preferPercent && termValues.percent != null) {
        snap[keys.percent] = termValues.percent;
        snap[keys.type] = RESIDUAL_PERCENT;
        return 'percent';
    }
    if (termValues.dollar != null) {
        snap[keys.amount] = termValues.dollar;
        snap[keys.type] = RESIDUAL_DOLLAR;
        return 'dollar';
    }
    return undefined;
};

const hydrateProgramResidual = (snap: OverridesFallbackSnapshot, preferPercent: boolean, term: PaymentTerm): void => {
    // Program residual has no separate type-override field — only the override value.
    if (preferPercent && term.programResidualPercent != null) {
        if (snap.programResidualPercentOverride == null) {
            snap.programResidualPercentOverride = term.programResidualPercent;
        }
        return;
    }
    if (term.programResidual != null && snap.programResidualOverride == null) {
        snap.programResidualOverride = term.programResidual;
    }
};

export const pickFallbackSnapshot = (node: IPencilOfferInputs, offer: Offer, offerType: OfferType): OverridesFallbackSnapshot => {
    const snap = copyExistingOverrides(node);

    if (!RATE_OFFER_TYPES.has(offerType)) return snap;

    const term = getSelectedTerm({ offer }, offerType) as PaymentTerm | undefined;
    if (!term) return snap;

    hydrateRate(snap, term, offerType);

    if (offerType === LEASE || offerType === BALLOON) {
        // Prefer percent for LEASE so the residual override survives a vehicle swap (dollar
        // values are tied to the prior vehicle's base). BALLOON residuals are intrinsically
        // dollar (the lump balloon payment), so keep dollar there.
        const preferPercent = offerType === LEASE;

        const baseUnit = hydrateResidualPair(
            snap,
            preferPercent,
            { percent: term.baseResidualPercent, dollar: term.baseResidualDollar },
            {
                amount: 'baseResidualAmountOverride',
                percent: 'baseResidualPercentOverride',
                type: 'baseResidualTypeOverride'
            }
        );

        if (snap.baseResidualType == null && baseUnit) {
            snap.baseResidualType = baseUnit === 'percent' ? RESIDUAL_PERCENT : RESIDUAL_DOLLAR;
        }

        hydrateProgramResidual(snap, preferPercent, term);

        hydrateResidualPair(
            snap,
            preferPercent,
            { percent: term.adjustedResidualPercent, dollar: term.adjustedResidualValue },
            {
                amount: 'adjustedResidualAmountOverride',
                percent: 'adjustedResidualPercentOverride',
                type: 'adjustedResidualTypeOverride'
            }
        );
    }

    if (offerType === BALLOON && snap.balloonPaymentAmountOverride == null && term.baseResidualDollar != null) {
        snap.balloonPaymentAmountOverride = term.baseResidualDollar;
    }

    return snap;
};

export const wipeOverridesNode = (node: IPencilOfferInputs, leaseMileage: number | undefined): IPencilOfferInputs => {
    return {
        ...node,
        annualMiles: leaseMileage,
        rateTypeOverride: undefined,
        sellRateOverride: undefined,
        buyRateOverride: undefined,
        securityDepositOverride: undefined,
        acqFeeOverride: undefined,
        acqFeeUpFrontOverride: undefined,
        adjustedResidualAmountOverride: undefined,
        adjustedResidualPercentOverride: undefined,
        adjustedResidualTypeOverride: undefined,
        baseResidualAmountOverride: undefined,
        baseResidualPercentOverride: undefined,
        baseResidualType: undefined,
        baseResidualTypeOverride: undefined,
        programResidualOverride: undefined,
        programResidualPercentOverride: undefined,
        manualIncentives: undefined,
        feesOverride: undefined,
        taxItems: [],
        taxOverrideUpfront: undefined,
        manualTotalTax: undefined,
        overridesByTerm: {},
        [OFFER_ENCRYPTION_FIELDS.OVERRIDES]: '',
        [OFFER_ENCRYPTION_FIELDS.INCENTIVES]: '',
        honorOverrides: false,
        overridesFallbackSnapshot: undefined
    };
};
